<template>
  <v-container v-if="$can('browse', 'NavigareCompany')">
    <!--<editor-fold desc="Page header">-->
    <v-row
      no-gutters
      align="center"
      class="mb-12"
    >
      <v-col>
        <div class="headline">
          Companies
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'NavigareCompany')"
          color="primary"
          class="ma-0"
          @click="openNavigareCompaniesEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.id"
          solo
          label="ID"
          hide-details
          clearable
          class="mb-2"
          autocomplete="no"
          type="number"
        />
        <v-text-field
          v-model="filtersDirty.name"
          solo
          label="Name"
          hide-details
          clearable
          class="mb-2"
          autocomplete="no"
        />
        <v-autocomplete
          v-model="filtersDirty.country_id"
          solo
          label="Country"
          :items="charteringCountries.data"
          :loading="charteringCountries.loading"
          item-text="name"
          item-value="id"
          hide-details
          clearable
        />
      </v-col>
      <v-col
        cols="13"
        class="align-end"
      >
        <!--<editor-fold desc="Filtering no results">-->
        <v-row
          v-if="!navigareCompanies.loading && !navigareCompanies.data.length && isFiltered"
          no-gutters
          class="text-center py-2"
        >
          <v-col cols="16">
            There are no results for your search..
          </v-col>
          <v-col
            cols="16"
            class="mt-2"
          >
            <v-btn
              class="ma-0"
              small
              text
              @click="clearFilters()"
            >
              <v-icon left>
                clear
              </v-icon>
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
        <!--</editor-fold>-->
        <!--<editor-fold desc="No data message">-->
        <v-card
          v-else-if="!navigareCompanies.loading && !navigareCompanies.data.length"
          class="radius-10 mb-6 elevation-5"
        >
          <v-card-text class="pa-6 text-center">
            <v-row>
              <v-col cols="16">
                There are no companies at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  v-if="$can('create', 'NavigareCompany')"
                  color="primary"
                  class="ma-0"
                  small
                  @click="openNavigareCompaniesEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <!--</editor-fold>-->
        <!--<editor-fold desc="Data">-->
        <div v-else>
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="navigareCompanies.meta.from"
            :to="navigareCompanies.meta.to"
            :total="navigareCompanies.meta.total"
            :last-page="navigareCompanies.meta.last_page"
            :page.sync="pagination.page"
            class="mb-1 align-end"
          />
          <!--</editor-fold>-->
          <!--<editor-fold desc="Loading">-->
          <v-row
            v-if="navigareCompanies.loading"
            class="justify-center"
          >
            <v-col
              class="pa-6 shrink"
            >
              <v-progress-circular
                :size="50"
                :width="4"
                indeterminate
              />
            </v-col>
          </v-row>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Data">-->
          <div v-else>
            <navigare-company-card
              v-for="navigareCompany in navigareCompanies.data"
              :key="navigareCompany.id"
              :navigare-company="navigareCompany"
              @editNavigareCompany="openNavigareCompaniesEditDialog"
            />
          </div>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="navigareCompanies.meta.from"
            :to="navigareCompanies.meta.to"
            :total="navigareCompanies.meta.total"
            :last-page="navigareCompanies.meta.last_page"
            :page.sync="pagination.page"
          />
          <!--</editor-fold>-->
        </div>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <navigare-company-edit-dialog
      :navigare-company="navigareCompanyDirty"
      :visible.sync="navigareCompanyEditDialog"
      @navigareCompany:created="navigareCompanyCreated"
      @navigareCompany:updated="navigareCompanyUpdated"
      @navigareCompany:deleted="navigareCompanyDeleted"
    />
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import NavigareCompany from '@/models/NavigareCompany'
import NavigareCompanyCard from '@/components/settings/navigareCompanies/NavigareCompanyCard'
import NavigareCompanyEditDialog from '@/components/settings/navigareCompanies/NavigareCompanyEditDialog'

export default {
    name: 'NavigareCompaniesIndex',
    components: {
      NavigareCompanyCard,
      NavigareCompanyEditDialog
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        navigareCompanies: {
          data: [],
          loading: false,
          meta: {}
        },
        navigareCompanyDirty: undefined,
        navigareCompanyEditDialog: false,
        filtersClean: NavigareCompany.emptyFilters,
        filtersDirty: cloneDeep(NavigareCompany.emptyFilters)
      }
    },
    computed: {
      ...mapGetters('settings', ['charteringCountries'])
    },
    created() {
      this.fetchNavigareCompanies()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchNavigareCompanies()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchNavigareCompanies() {
        this.navigareCompanies.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await NavigareCompany.api.fetch(queryParams)

          this.navigareCompanies.data = result.data
          this.navigareCompanies.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.navigareCompanies.loading = false
        }
      },
      openNavigareCompaniesEditDialog(navigareCompany = undefined) {
        this.navigareCompanyDirty = cloneDeep(navigareCompany)
        this.navigareCompanyEditDialog = true
      },
      clearFilters() {
        this.filters = cloneDeep(NavigareCompany.emptyFilters)
      },
      navigareCompanyCreated(navigareCompany) {
        this.navigareCompanies.data.unshift(navigareCompany)
      },
      navigareCompanyUpdated(navigareCompany) {
        const index = this.navigareCompanies.data.findIndex(nc => nc.id === navigareCompany.id)

        this.navigareCompanies.data.splice(index, 1, navigareCompany)
      },
      navigareCompanyDeleted(navigareCompanyId) {
        const index = this.navigareCompanies.data.findIndex(nc => nc.id === navigareCompanyId)

        this.navigareCompanies.data.splice(index, 1)
      }
    }
  }
</script>
